import { ContextMetricEnum } from '@shared/api/types';
import Text from '@shared/components/content/text';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import GuideTemplate from '@shared/components/guide-template';
import { FormLabel } from '@shared/components/ui/form';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  metricOptions,
  metricUnits,
  NO_METRIC_OPTION,
} from '../options/metric-options';
import { getReportsPathMap } from '../utils';
import {
  CreateReportContext,
  CreateReportFormValues,
} from './create-report-layout';

export default function CreateReportMetricPage() {
  const navigate = useNavigate();
  const { onSubmit, isSaving, isEditing, reportType } =
    useOutletContext<CreateReportContext>();
  const { trigger, handleSubmit, watch } =
    useFormContext<CreateReportFormValues>();

  const onCompleteFlow = async () => {
    const isValid = await trigger();
    isValid && handleSubmit(onSubmit)();
  };

  const selectedOption = watch(`contextMetric`);
  const option = metricOptions.find((o) => o.value === selectedOption);

  return (
    <GuideTemplate
      preTitle={`${isEditing ? 'Edit' : 'New'} ${reportType} report`}
      title="Add context to your emissions"
      description={
        <Text>
          We can contextualise your total emissions by normalising the total
          figure with an additional business metric, such as money spent or
          number of people served. This can help you to understand your
          emissions independently of business fluctuations.
        </Text>
      }
      primaryActionText={`${isEditing ? 'Update' : 'Create'} report`}
      secondaryActionText="Back"
      primaryActionProps={{
        onClick: onCompleteFlow,
        type: 'submit',
        loading: isSaving,
        disabled: isSaving,
        'data-testid': 'create-report-submit-button',
      }}
      secondaryActionProps={{
        onClick: () => navigate(getReportsPathMap()[reportType!].setup),
      }}
    >
      <form className="w-[360px] text-left">
        <fieldset disabled={isSaving} className="space-y-4">
          <div className="space-y-1">
            <FormLabel className="text-left">
              How do you want to contextualise your emissions?
            </FormLabel>
            <div className="flex items-center gap-2">
              <SelectFormField
                name="contextMetric"
                options={metricOptions}
                className="w-full"
                testId="context-metric-select"
              />
            </div>
          </div>
          {selectedOption && selectedOption !== NO_METRIC_OPTION && (
            <div className="grid w-full grid-cols-2 gap-4">
              {(selectedOption === ContextMetricEnum.money_spent ||
                selectedOption === ContextMetricEnum.money_earned) && (
                <div className="space-y-1">
                  <FormLabel className="text-left">Currency</FormLabel>
                  <SelectFormField
                    name="contextMetricUnit"
                    options={metricUnits}
                    className="w-full"
                  />
                </div>
              )}
              <div className="space-y-1">
                <FormLabel className="text-left">
                  {option?.metricLabel || 'Metric value'}
                </FormLabel>
                <InputFormField
                  name="contextMetricValue"
                  type="number"
                  min={1}
                  step={1}
                  className="w-full"
                  data-testid="context-metric-value-input"
                />
              </div>
            </div>
          )}
        </fieldset>
      </form>
    </GuideTemplate>
  );
}
